import { useState, useEffect } from 'react';
import PhoneInput, { getCountryCallingCode, parsePhoneNumber, isValidPhoneNumber, isPossiblePhoneNumber } from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en';
import 'react-phone-number-input/style.css';
import { ChevronDown, CheckCircle, AlertCircle } from 'lucide-react';

// Country codes we support with their names
const COUNTRIES = [
  { code: 'IN', name: 'India' },
  { code: 'AE', name: 'UAE' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'QA', name: 'Qatar' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'OM', name: 'Oman' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'NP', name: 'Nepal' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'TH', name: 'Thailand' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'PH', name: 'Philippines' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'CN', name: 'China' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AT', name: 'Austria' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'RU', name: 'Russia' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'PL', name: 'Poland' },
  { code: 'TR', name: 'Turkey' },
  { code: 'EG', name: 'Egypt' },
  { code: 'KE', name: 'Kenya' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'GH', name: 'Ghana' }
];

// Get flag emoji from country code
const getFlagEmoji = (countryCode) => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

const PhoneInputCustom = ({ value, onChange, defaultCountry = 'IN', placeholder = 'Enter phone number', showValidation = true, ...props }) => {
  const [country, setCountry] = useState(defaultCountry);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [validationStatus, setValidationStatus] = useState(null); // null, 'valid', 'invalid', 'partial'

  // Validate phone number using libphonenumber-js
  const validatePhone = (fullNumber) => {
    if (!fullNumber || fullNumber.length < 4) {
      setValidationStatus(null);
      return;
    }
    try {
      if (isValidPhoneNumber(fullNumber)) {
        setValidationStatus('valid');
      } else if (isPossiblePhoneNumber(fullNumber)) {
        setValidationStatus('partial'); // Possible but not yet valid (might need more digits)
      } else {
        setValidationStatus('invalid');
      }
    } catch (e) {
      setValidationStatus(null);
    }
  };

  useEffect(() => {
    // Parse initial value if provided
    if (value) {
      try {
        const parsed = parsePhoneNumber(value);
        if (parsed) {
          setCountry(parsed.country || defaultCountry);
          setPhoneNumber(parsed.nationalNumber || '');
        }
        validatePhone(value);
      } catch (e) {
        // Value might not be a valid phone number yet
      }
    }
  }, []);

  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    setCountry(newCountry);
    // Update the full phone number with new country code
    if (phoneNumber) {
      const newValue = `+${getCountryCallingCode(newCountry)}${phoneNumber}`;
      onChange(newValue);
      validatePhone(newValue);
    }
  };

  const handlePhoneChange = (e) => {
    const newNumber = e.target.value.replace(/\D/g, ''); // Only digits
    setPhoneNumber(newNumber);
    if (newNumber) {
      const fullNumber = `+${getCountryCallingCode(country)}${newNumber}`;
      onChange(fullNumber);
      validatePhone(fullNumber);
    } else {
      onChange('');
      setValidationStatus(null);
    }
  };

  const callingCode = `+${getCountryCallingCode(country)}`;

  // Determine border color based on validation - removed ring styles to eliminate borders
  const getBorderClass = () => {
    return ''; // No border classes - validation shown via icon only
  };

  return (
    <div className="phone-input-container">
      <div className={`phone-country-box ${getBorderClass()}`}>
        <span className="phone-flag">{getFlagEmoji(country)}</span>
        <span className="phone-calling-code">{callingCode}</span>
        <ChevronDown size={16} className="phone-arrow" />
        <select 
          value={country} 
          onChange={handleCountryChange}
          className="phone-country-select"
        >
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {getFlagEmoji(c.code)} {c.name} (+{getCountryCallingCode(c.code)})
            </option>
          ))}
        </select>
      </div>
      <div className="phone-input-wrapper">
        <input
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder={placeholder}
          className={`phone-number-input ${getBorderClass()}`}
          {...props}
        />
        {showValidation && phoneNumber && (
          <span className="phone-validation-icon">
            {validationStatus === 'valid' && <CheckCircle size={18} className="text-green-500" />}
            {validationStatus === 'invalid' && <AlertCircle size={18} className="text-red-500" />}
          </span>
        )}
      </div>
      {showValidation && validationStatus === 'invalid' && phoneNumber && (
        <p className="phone-validation-message text-red-500 text-xs mt-1">
          Please enter a valid phone number for the selected country
        </p>
      )}
    </div>
  );
};

export default PhoneInputCustom;
